import { Calculator, Atom, FlaskConical, Leaf, BookOpen, Code, Landmark, Music } from "lucide-react";

export const subjects = [
  {
    slug: "mathematics",
    name: "Mathematics",
    icon: "Calculator",
    color: "bg-blue-50 text-blue-600",
    border: "border-blue-100",
    accent: "#3B82F6",
    shortDesc: "Algebra, Calculus, Statistics & more",
    fullDesc: "Master mathematical concepts from basic arithmetic to advanced calculus. Our expert tutors break down complex problems into digestible steps, building strong foundations in algebra, geometry, trigonometry, statistics, and beyond. Whether you're preparing for board exams or competitive tests, we tailor every session to your pace and goals.",
    topics: ["Algebra", "Calculus", "Statistics", "Geometry", "Trigonometry", "Number Theory", "Probability", "Linear Algebra"],
    levels: ["Grade 1-5", "Grade 6-8", "Grade 9-10", "Grade 11-12", "College", "Competitive Exams"],
    whyLearn: "Mathematics is the foundation of science, engineering, economics, and technology. Strong math skills open doors to top universities and high-paying careers.",
    careerPaths: ["Engineering", "Data Science", "Finance", "Research", "Architecture"],
  },
  {
    slug: "physics",
    name: "Physics",
    icon: "Atom",
    color: "bg-violet-50 text-violet-600",
    border: "border-violet-100",
    accent: "#8B5CF6",
    shortDesc: "Mechanics, Thermodynamics, Optics",
    fullDesc: "Explore the fundamental laws governing the universe. From classical mechanics to quantum physics, our tutors use real-world examples and interactive demonstrations to make abstract concepts tangible. Perfect for students aiming for engineering entrance exams or deepening their understanding of how the world works.",
    topics: ["Classical Mechanics", "Thermodynamics", "Optics", "Electromagnetism", "Modern Physics", "Waves", "Nuclear Physics"],
    levels: ["Grade 9-10", "Grade 11-12", "College", "Competitive Exams"],
    whyLearn: "Physics develops critical thinking and problem-solving skills. It's essential for careers in engineering, medicine, aerospace, and cutting-edge technology.",
    careerPaths: ["Engineering", "Aerospace", "Medical Physics", "Research", "Renewable Energy"],
  },
  {
    slug: "chemistry",
    name: "Chemistry",
    icon: "FlaskConical",
    color: "bg-emerald-50 text-emerald-600",
    border: "border-emerald-100",
    accent: "#10B981",
    shortDesc: "Organic, Inorganic & Physical Chemistry",
    fullDesc: "Dive into the molecular world with comprehensive chemistry tutoring. From balancing equations to understanding organic reaction mechanisms, our tutors make chemistry engaging and intuitive. We cover physical, organic, and inorganic chemistry with practical applications and exam-focused problem solving.",
    topics: ["Organic Chemistry", "Inorganic Chemistry", "Physical Chemistry", "Biochemistry", "Analytical Chemistry", "Electrochemistry"],
    levels: ["Grade 9-10", "Grade 11-12", "College", "Competitive Exams"],
    whyLearn: "Chemistry is central to medicine, pharmaceuticals, materials science, and environmental studies. Understanding chemistry opens up diverse career opportunities.",
    careerPaths: ["Pharmacy", "Medicine", "Chemical Engineering", "Environmental Science", "Materials Science"],
  },
  {
    slug: "biology",
    name: "Biology",
    icon: "Leaf",
    color: "bg-green-50 text-green-600",
    border: "border-green-100",
    accent: "#22C55E",
    shortDesc: "Botany, Zoology, Genetics & Ecology",
    fullDesc: "Discover the science of life from cells to ecosystems. Our biology tutors bring the subject alive with vivid explanations of genetics, evolution, human anatomy, ecology, and molecular biology. Ideal for medical aspirants, biology enthusiasts, and students preparing for life science exams.",
    topics: ["Cell Biology", "Genetics", "Human Anatomy", "Ecology", "Evolution", "Microbiology", "Botany", "Zoology"],
    levels: ["Grade 6-8", "Grade 9-10", "Grade 11-12", "College", "Competitive Exams"],
    whyLearn: "Biology is the gateway to medicine, biotechnology, environmental conservation, and healthcare. It helps you understand the living world around you.",
    careerPaths: ["Medicine", "Biotechnology", "Environmental Science", "Genetics Research", "Veterinary Science"],
  },
  {
    slug: "english",
    name: "English",
    icon: "BookOpen",
    color: "bg-amber-50 text-amber-600",
    border: "border-amber-100",
    accent: "#F59E0B",
    shortDesc: "Literature, Grammar & Creative Writing",
    fullDesc: "Strengthen your command of the English language through literature analysis, grammar mastery, and creative expression. Our tutors help with essay writing, comprehension, vocabulary building, and SAT/IELTS/TOEFL preparation. From Shakespeare to modern literature, we make reading and writing a joy.",
    topics: ["Grammar", "Literature", "Creative Writing", "Essay Writing", "Vocabulary", "Comprehension", "Public Speaking"],
    levels: ["Grade 1-5", "Grade 6-8", "Grade 9-10", "Grade 11-12", "College", "Test Prep"],
    whyLearn: "English proficiency is essential for global communication, academic success, and professional growth. Strong language skills boost confidence in every area of life.",
    careerPaths: ["Journalism", "Content Writing", "Education", "Law", "Public Relations"],
  },
  {
    slug: "computer-science",
    name: "Computer Science",
    icon: "Code",
    color: "bg-cyan-50 text-cyan-600",
    border: "border-cyan-100",
    accent: "#06B6D4",
    shortDesc: "Programming, Data Structures, AI",
    fullDesc: "Learn to code and think computationally with expert guidance. From Python basics to advanced algorithms, data structures, machine learning, and web development, our CS tutors prepare you for the digital future. Hands-on projects and real-world applications keep every session engaging.",
    topics: ["Python", "Java", "Data Structures", "Algorithms", "Machine Learning", "Web Development", "Databases", "AI Fundamentals"],
    levels: ["Grade 6-8", "Grade 9-10", "Grade 11-12", "College", "Competitive Exams"],
    whyLearn: "Computer Science is the fastest-growing field globally. Programming skills are in demand across every industry, offering excellent career prospects.",
    careerPaths: ["Software Engineering", "Data Science", "AI/ML Engineering", "Cybersecurity", "Product Management"],
  },
  {
    slug: "history",
    name: "History",
    icon: "Landmark",
    color: "bg-rose-50 text-rose-600",
    border: "border-rose-100",
    accent: "#F43F5E",
    shortDesc: "World History, Civics & Geography",
    fullDesc: "Journey through time and understand the events that shaped our world. Our history tutors bring the past to life with storytelling, critical analysis, and contextual understanding. From ancient civilizations to modern geopolitics, we cover it all with engaging sessions.",
    topics: ["Ancient History", "Medieval History", "Modern History", "World Wars", "Indian History", "Civics", "Geography"],
    levels: ["Grade 6-8", "Grade 9-10", "Grade 11-12", "College", "Competitive Exams"],
    whyLearn: "History develops analytical thinking, helps understand current events, and is crucial for civil services, law, and humanities careers.",
    careerPaths: ["Civil Services", "Law", "Journalism", "Archaeology", "Museum Curation"],
  },
  {
    slug: "music",
    name: "Music",
    icon: "Music",
    color: "bg-orange-50 text-orange-600",
    border: "border-orange-100",
    accent: "#F97316",
    shortDesc: "Theory, Instruments & Composition",
    fullDesc: "Unlock your musical potential with personalized lessons in theory, instruments, and composition. Whether you're a beginner picking up your first instrument or an intermediate student preparing for grade exams, our music tutors nurture your talent with patience and expertise.",
    topics: ["Music Theory", "Piano", "Guitar", "Vocals", "Composition", "Music Production", "Ear Training"],
    levels: ["Beginner", "Intermediate", "Advanced", "Grade Exams"],
    whyLearn: "Music enhances creativity, emotional intelligence, and cognitive abilities. It's a lifelong skill that brings joy and can lead to fulfilling careers.",
    careerPaths: ["Music Performance", "Music Production", "Sound Engineering", "Music Education", "Film Scoring"],
  },
];

export const subjectIcons = {
  Calculator, Atom, FlaskConical, Leaf, BookOpen, Code, Landmark, Music,
};
